import { toast, ToastOptions, TypeOptions } from 'react-toastify'
import { ToastErrorRenderer } from '../components/ToastErrorRenderer'
import React, { useEffect, useRef, useState } from 'react'
import { Button } from '../components/shadcn/Button'
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface ToastPromiseOptions {
  pending: string
  success:
    | string
    | {
        render: (data: any) => React.ReactNode
        [key: string]: any
      }
  error:
    | string
    | {
        render?: (data: any) => React.ReactNode
        [key: string]: any
      }
}

/**
 * Enhanced version of toast.promise that simplifies error rendering
 * using the ToastErrorRenderer component
 *
 * @param promise The promise to track
 * @param messages Object containing pending, success, and error messages
 * @param options Additional toast options
 * @returns The original promise
 */
export function toastPromise<T>(
  promise: Promise<T>,
  messages: {
    pending: string
    success: string | { render?: (data: T) => React.ReactNode; [key: string]: any }
    error: string
  },
  options?: { autoClose?: number | false; type?: TypeOptions; position?: any },
): Promise<T> {
  return toast.promise(
    promise,
    {
      pending: messages.pending,
      success: typeof messages.success === 'string' ? messages.success : messages.success,
      error: {
        position: 'top-center',
        autoClose: false,
        draggable: false,
        closeOnClick: false,
        render({ data }) {
          return <ToastErrorRenderer data={data} message={messages.error} />
        },
        ...options,
      },
    },
    options,
  )
}

export function toastSuccess(message: string, options?: ToastOptions) {
  toast.success(message, options)
}

export function toastInfo(message: string, options?: ToastOptions) {
  toast.info(message, options)
}

export function toastWarning(message: string, options?: ToastOptions) {
  toast.warning(message, {
    position: 'top-center',
    autoClose: 5000,
    draggable: false,
    closeOnClick: true,
    ...options,
  })
}

export function toastError(message: string, options?: ToastOptions) {
  toast.error(message, {
    position: 'top-center',
    autoClose: false,
    draggable: false,
    closeOnClick: false,
    ...options,
  })
}

/**
 * Helper for handling a Promise.all array with proper toast error handling
 *
 * @param promises Array of promises to resolve
 * @returns Promise resolving to the array of results
 */
export function handlePromiseAllToast<T>(promises: Promise<T>[]): Promise<T[]> {
  return Promise.all(promises).then(results => {
    // Handle errors in the results (e.g., GraphQL errors)
    const hasErrors = results.some((r: any) => r?.errors)
    if (hasErrors) {
      const errorResult = results.find((r: any) => r?.errors)
    }
    return results
  })
}

export function CloseToastButton({ closeToast }) {
  return (
    <Button variant="ghost" size="lg" onClick={closeToast} className="ml-3 hover:bg-transparent">
      <FontAwesomeIcon icon={faXmark} size="lg" />
    </Button>
  )
}

// i really do not know how to do it better :( \ бля я правда не шарю как можно было сделать это лучше, я два дня потратил на ресёрч сори

const findLowestToastHeight = (): number => {
  const toasts = [...document.querySelectorAll('.Toastify__toast--error')]
  if (toasts.length === 0) return 0

  return toasts.map(toast => toast.getBoundingClientRect().y).reduce((max, y) => Math.max(max, y), 0)
}

const getNotAnimatedToastCount = (): number => {
  return document.querySelectorAll('.Toastify__toast--error:not(.Toastify__bounce-exit--top-center)')?.length ?? 0
}

const getAllToastsCount = (): number => {
  return document.querySelectorAll('.Toastify__toast--error')?.length ?? 0
}

export function ToastCloseAllButton() {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const allToastCount = useRef(0)
  const notAnimatedToastCount = useRef(0)

  useEffect(() => {
    const interval = setInterval(() => {
      const prevAllToastCount = allToastCount.current
      allToastCount.current = getAllToastsCount()
      notAnimatedToastCount.current = getNotAnimatedToastCount()

      const button = buttonRef.current
      if (!button) return

      // Handle visibility changes
      if (prevAllToastCount !== allToastCount.current) {
        button.style.display = allToastCount.current > 1 ? 'block' : 'none'
        return
      }

      // Calculate position
      const lowestToastHeight = findLowestToastHeight()
      const buttonWidth = button.getBoundingClientRect().width
      const screenCenter = window.innerWidth / 2

      // Apply positioning and styling
      button.style.transform = `translateY(${lowestToastHeight + 80}px) translateX(${screenCenter - buttonWidth / 2}px)`
      button.style.cursor = notAnimatedToastCount.current > 1 ? 'pointer' : 'default'
      button.style.opacity = notAnimatedToastCount.current > 1 ? '1' : '0'
    }, 5)

    return () => clearInterval(interval)
  }, [])

  return (
    <Button
      variant="destructive"
      size="lg"
      ref={buttonRef}
      onClick={() => toast.dismiss()}
      className="absolute top-0 left-0 transition-opacity duration-150 ease-in-out opacity-0">
      Zavřít vše
    </Button>
  )
}
